<!-- Loading State -->
@if (loading) {
  <div class="container-xxl py-20">
    <div class="text-center">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">{{ 'eventDetails.loading' | translate }}</span>
      </div>
      <p class="text-muted mt-5">{{ 'eventDetails.loading' | translate }}</p>
    </div>
  </div>
}

<!-- Event Details -->
@if (!loading && event) {
  <div class="event-details-page container" style="max-width: 1400px">
    <!-- Event Header Banner -->
    <div class="event-header" [style.background-image]="event.header_url ? 'url(' + event.header_url + ')' : 'none'">
      <div class="event-header-overlay">
        <div class="container-xxl" style="max-width: 1400px;">
          <div class="event-header-content">
            <h1 class="event-title" style="color: white">{{ event.event_name }}</h1>
            <div class="event-meta">
              @if (event.company) {
                @if (event.company.is_personal) {
                  <span class="event-meta-item">
                    <i class="fa fa-user me-2"></i>
                    {{ event.company.company_name }}
                  </span>
                } @else {
                  <a class="event-meta-item organiser-link" (click)="navigateToOrganisation()">
                    <i class="fa fa-building me-2"></i>
                    {{ event.company.company_name }}
                  </a>
                }
              }
              @if (event.start_date) {
                <span class="event-meta-item">
                  <i class="fa fa-calendar me-2"></i>
                  {{ formatDateDMYDots(event.start_date) }}
                  @if (event.end_date && event.start_date !== event.end_date) {
                    - {{ formatDateDMYDots(event.end_date) }}
                  }
                </span>
              }
              <span class="event-meta-item">
                <i class="fa fa-location-dot me-2"></i>
                {{ event.city }}, {{ event.country }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- View Toggle Header -->
    <div class="view-toggle-wrapper">
      <div class="view-toggle">
        <button class="view-toggle-btn" [class.active]="currentView === 'information'" (click)="setView('information')">
          <i class="fa fa-info-circle me-2"></i>{{ 'eventDetails.viewInformation' | translate }}
        </button>
        <button class="view-toggle-btn" [class.active]="currentView === 'announcements'" (click)="setView('announcements')">
          <i class="fa fa-bullhorn me-2"></i>{{ 'eventDetails.viewAnnouncements' | translate }}
          @if (announcements.length > 0) {
            <span class="badge badge-light-danger ms-2">{{ announcements.length }}</span>
          }
        </button>
        @if (isCompetitionEvent && !isSocialEvent) {
          <button class="view-toggle-btn" [class.active]="currentView === 'results'" (click)="setView('results')">
            <i class="fa fa-trophy me-2"></i>{{ 'eventDetails.viewResults' | translate }}
          </button>
        }
      </div>
    </div>

    @if (currentView === 'information') {
      <!-- Section Navigation Tabs -->
      <div class="section-nav-wrapper" id="sectionNav">
        <div class="section-nav">
          <button class="section-nav-btn" [class.active]="activeSection === 'overview'" (click)="scrollToSection('overview')">
            <i class="fa fa-info-circle me-2"></i>{{ 'eventDetails.navOverview' | translate }}
          </button>
          @if (isCompetitionEvent && !isSocialEvent) {
            <button class="section-nav-btn" [class.active]="activeSection === 'schedules'" (click)="scrollToSection('schedules')">
              <i class="fa fa-calendar-days me-2"></i>{{ 'eventDetails.navSchedule' | translate }}
            </button>
          }
          <!-- TEMPORARILY DISABLED FOR V1
          @if (isCompetitionEvent && !isSocialEvent && hasLiveSchedules()) {
            <button class="section-nav-btn live-btn" [class.active]="activeSection === 'live'" (click)="scrollToSection('live')">
              <i class="fa fa-broadcast-tower text-danger me-2 animate-pulse"></i>
              <span class="text-danger">{{ 'eventDetails.navLive' | translate }}</span>
            </button>
          }
          -->
          @if (isCompetitionEvent && !isSocialEvent) {
            <button class="section-nav-btn" [class.active]="activeSection === 'classes'" (click)="scrollToSection('classes')">
              <i class="fa fa-list me-2"></i>{{ 'eventDetails.navClasses' | translate }}
              <span class="badge badge-light-primary ms-2">{{ eventClasses.length }}</span>
            </button>
          }
          @if (isTrainingEvent) {
            <button class="section-nav-btn" [class.active]="activeSection === 'packages'" (click)="scrollToSection('packages')">
              <i class="fa fa-box-open me-2"></i>{{ 'eventDetails.navPackages' | translate }}
              <span class="badge badge-light-primary ms-2">{{ trainingPackages.length }}</span>
            </button>
            @if (trainingSessions.length > 0) {
              <button class="section-nav-btn" [class.active]="activeSection === 'training-schedule'" (click)="scrollToSection('training-schedule')">
                <i class="fa fa-calendar-days me-2"></i>{{ 'eventDetails.navSchedule' | translate }}
                <span class="badge badge-light-primary ms-2">{{ trainingSessions.length }}</span>
              </button>
            }
          }
          @if (eventServices.length > 0) {
            <button class="section-nav-btn" [class.active]="activeSection === 'services'" (click)="scrollToSection('services')">
              <i class="fa fa-concierge-bell me-2"></i>{{ 'eventDetails.navServices' | translate }}
            </button>
          }
          @if (eventContacts.length > 0) {
            <button class="section-nav-btn" [class.active]="activeSection === 'contacts'" (click)="scrollToSection('contacts')">
              <i class="fa fa-address-book me-2"></i>{{ 'eventDetails.navContacts' | translate }}
            </button>
          }
        </div>
      </div>
    }

    @if (currentView === 'announcements') {
      <!-- Announcements View -->
      <div class="container-xxl py-10" style="max-width: 1000px;">
        @if (loadingAnnouncements) {
          <div class="text-center py-10">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">{{ 'eventDetails.loadingAnnouncements' | translate }}</span>
            </div>
            <p class="text-muted mt-3">{{ 'eventDetails.loadingAnnouncements' | translate }}</p>
          </div>
        } @else if (announcements.length === 0) {
          <div class="text-center py-10">
            <i class="fa fa-bullhorn fs-3x text-muted mb-5 d-block"></i>
            <h4 class="text-muted">{{ 'eventDetails.noAnnouncements' | translate }}</h4>
            <p class="text-muted">{{ 'eventDetails.noAnnouncementsHint' | translate }}</p>
          </div>
        } @else {
          <div class="announcements-feed">
            @for (announcement of announcements; track announcement.announcement_id) {
              <div class="announcement-card-public">
                <div class="announcement-header-public">
                  <h3 class="announcement-title-public">{{ announcement.title }}</h3>
                  <span class="announcement-time-public">
                    <i class="fa fa-clock me-1"></i>
                    {{ formatAnnouncementDate(announcement.created_at) }}
                  </span>
                </div>
                @if (announcement.description) {
                  <div class="announcement-body-public">
                    <p>{{ announcement.description }}</p>
                  </div>
                }
                @if (announcement.images && announcement.images.length > 0) {
                  <div class="announcement-images-public">
                    @for (image of announcement.images; track image.image_id) {
                      <div class="announcement-image-public" (click)="openLightbox(image.image_url!)">
                        <img [src]="image.image_url" alt="Announcement image">
                        <div class="image-zoom-hint">
                          <i class="fa fa-search-plus"></i>
                        </div>
                      </div>
                    }
                  </div>
                }
              </div>
            }
          </div>
        }
      </div>
    }

    @if (currentView === 'results') {
      <!-- Results View -->
      <div class="container-xxl py-10" style="max-width: 1200px;">
        @if (loadingResults) {
          <div class="text-center py-10">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">{{ 'eventDetails.loadingResults' | translate }}</span>
            </div>
            <p class="text-muted mt-3">{{ 'eventDetails.loadingResults' | translate }}</p>
          </div>
        } @else if (classResults.length === 0) {
          <div class="d-flex flex-column align-items-center justify-content-center py-10">
            <i class="fa fa-trophy fs-3x text-muted mb-5"></i>
            <h4 class="text-muted">{{ 'eventDetails.noResults' | translate }}</h4>
            <p class="text-muted">{{ 'eventDetails.noResultsHint' | translate }}</p>
          </div>
        } @else {
          <div class="results-feed">
            @for (classResult of classResults; track classResult.event_class_id) {
              <div class="card results-card">
                <div class="card-header results-card-header" (click)="toggleClassResults(classResult.event_class_id)">
                  <div class="d-flex align-items-center gap-3">
                    <div class="results-class-icon">
                      <i class="fa fa-flag-checkered"></i>
                    </div>
                    <div>
                      <h4 class="results-class-title mb-0">{{ classResult.class_name }}</h4>
                      <span class="text-muted fs-7">{{ classResult.results?.length || 0 }} {{ 'eventDetails.entries' | translate }}</span>
                    </div>
                  </div>
                  <i class="fa fa-chevron-down results-chevron" [class.rotated]="expandedClassResults.has(classResult.event_class_id)"></i>
                </div>
                @if (expandedClassResults.has(classResult.event_class_id)) {
                  <div class="card-body p-0">
                    @if (classResult.results && classResult.results.length > 0) {
                      <!-- Podium for top 3 -->
                      @if (classResult.results.length >= 3) {
                        <div class="results-podium">
                          <!-- 2nd Place -->
                          <div class="podium-place podium-second">
                            <div class="podium-medal silver">
                              <i class="fa fa-medal"></i>
                            </div>
                            <div class="podium-info">
                              <span class="podium-rider">{{ classResult.results[1].rider_name }}</span>
                              <span class="podium-horse">{{ classResult.results[1].horse_name }}</span>
                              <span class="podium-score">{{ getResultScore(classResult.results[1]) }}</span>
                            </div>
                            <div class="podium-block second"></div>
                          </div>
                          <!-- 1st Place -->
                          <div class="podium-place podium-first">
                            <div class="podium-medal gold">
                              <i class="fa fa-trophy"></i>
                            </div>
                            <div class="podium-info">
                              <span class="podium-rider">{{ classResult.results[0].rider_name }}</span>
                              <span class="podium-horse">{{ classResult.results[0].horse_name }}</span>
                              <span class="podium-score">{{ getResultScore(classResult.results[0]) }}</span>
                            </div>
                            <div class="podium-block first"></div>
                          </div>
                          <!-- 3rd Place -->
                          <div class="podium-place podium-third">
                            <div class="podium-medal bronze">
                              <i class="fa fa-award"></i>
                            </div>
                            <div class="podium-info">
                              <span class="podium-rider">{{ classResult.results[2].rider_name }}</span>
                              <span class="podium-horse">{{ classResult.results[2].horse_name }}</span>
                              <span class="podium-score">{{ getResultScore(classResult.results[2]) }}</span>
                            </div>
                            <div class="podium-block third"></div>
                          </div>
                        </div>
                      }
                      <!-- Full Results Table -->
                      <div class="table-responsive">
                        <table class="table table-hover results-table mb-0">
                          <thead>
                            <tr class="bg-light">
                              <th class="ps-4" style="width: 60px;">{{ 'eventDetails.resultsPlace' | translate }}</th>
                              <th>{{ 'eventDetails.resultsRider' | translate }}</th>
                              <th class="d-none d-md-table-cell">{{ 'eventDetails.resultsHorse' | translate }}</th>
                              <!-- Show round columns if there are multiple rounds -->
                              @if (hasMultipleRounds(classResult)) {
                                @for (rr of getRoundHeaders(classResult); track rr.round_number) {
                                  <th class="text-end d-none d-lg-table-cell">
                                    @if (rr.round_name) {
                                      {{ rr.round_name }}
                                    } @else {
                                      {{ 'eventDetails.round' | translate }} {{ rr.round_number }}
                                    }
                                  </th>
                                }
                                @if (classResult.final_result_type === 'combined_total') {
                                  <th class="text-end pe-4">{{ 'eventDetails.totalTime' | translate }}</th>
                                } @else {
                                  <th class="text-end pe-4">{{ 'eventDetails.bestTime' | translate }}</th>
                                }
                              } @else {
                                <th class="text-end pe-4">{{ 'eventDetails.resultsTime' | translate }}</th>
                              }
                            </tr>
                          </thead>
                          <tbody>
                            @for (result of classResult.results; track result.result_id; let i = $index) {
                              <tr>
                                <td class="ps-4">
                                  <div class="position-badge-wrapper">
                                    @if (i === 0) {
                                      <span class="position-badge gold"><i class="fa fa-trophy"></i></span>
                                    } @else if (i === 1) {
                                      <span class="position-badge silver"><i class="fa fa-medal"></i></span>
                                    } @else if (i === 2) {
                                      <span class="position-badge bronze"><i class="fa fa-award"></i></span>
                                    } @else {
                                      <span class="position-badge default">{{ i + 1 }}</span>
                                    }
                                  </div>
                                </td>
                                <td>
                                  <div class="rider-info">
                                    <span class="rider-name-cell">{{ result.rider_name }}</span>
                                    <span class="horse-name-cell d-md-none">{{ result.horse_name }}</span>
                                    <!-- Show round results on smaller screens as pills -->
                                    @if (result.round_results && result.round_results.length > 0) {
                                      <div class="round-results-mobile d-block d-lg-none mt-1">
                                        @for (rr of result.round_results; track rr.round_number) {
                                          <span class="badge badge-light-secondary me-1 mb-1">
                                            {{ rr.round_name || ('eventDetails.roundShort' | translate) + rr.round_number }}:
                                            @if (rr.final_time_seconds || rr.raw_time_seconds) {
                                              {{ formatResultTime(rr.final_time_seconds || rr.raw_time_seconds) }}
                                            } @else if (rr.final_points !== null) {
                                              {{ rr.final_points }} pts
                                            } @else if (rr.status && rr.status !== 'recorded' && rr.status !== 'pending') {
                                              {{ rr.status | uppercase }}
                                            } @else {
                                              -
                                            }
                                            @if (rr.image_count && rr.image_count > 0) {
                                              <i class="fa fa-images ms-1 cursor-pointer" (click)="viewRoundResultImages(rr, $event)"></i>
                                            }
                                          </span>
                                        }
                                      </div>
                                    }
                                  </div>
                                </td>
                                <td class="d-none d-md-table-cell text-muted">{{ result.horse_name }}</td>
                                <!-- Show round result columns on desktop -->
                                @if (result.round_results && result.round_results.length > 0) {
                                  @for (rr of result.round_results; track rr.round_number) {
                                    <td class="text-end d-none d-lg-table-cell text-muted">
                                      @if (rr.final_time_seconds || rr.raw_time_seconds) {
                                        {{ formatResultTime(rr.final_time_seconds || rr.raw_time_seconds) }}@if (rr.penalty_total > 0) {<span class="round-penalty">+{{ rr.penalty_total }}s</span>}
                                        @if (rr.image_count && rr.image_count > 0) {
                                          <i class="fa fa-images round-image-icon" (click)="viewRoundResultImages(rr, $event)" title="View images"></i>
                                        }
                                      } @else if (rr.final_points !== null && rr.final_points !== undefined) {
                                        {{ rr.final_points }} pts
                                      } @else if (rr.status && rr.status !== 'recorded' && rr.status !== 'pending') {
                                        <span class="round-status {{ rr.status }}">{{ rr.status | uppercase }}</span>
                                      } @else {
                                        -
                                      }
                                    </td>
                                  }
                                  <!-- Best time column when there are multiple rounds -->
                                  <td class="text-end pe-4">
                                    <div class="score-cell">
                                      @if (result.final_time_seconds || result.raw_time_seconds) {
                                        <span class="score-value">{{ formatResultTime(result.final_time_seconds || result.raw_time_seconds) }}</span>
                                        @if (result.penalty_total > 0) {
                                          <span class="penalty-badge">+{{ result.penalty_total }}s</span>
                                        }
                                      } @else if (result.final_points !== null && result.final_points !== undefined) {
                                        <span class="score-value">{{ result.final_points }} pts</span>
                                      } @else if (result.status && result.status !== 'recorded' && result.status !== 'pending') {
                                        <span class="status-badge-result {{ result.status }}">{{ result.status | uppercase }}</span>
                                      } @else {
                                        <span class="text-muted">-</span>
                                      }
                                      @if (result.image_count && result.image_count > 0) {
                                        <button class="btn btn-sm btn-icon btn-light-primary ms-2 image-icon-btn" (click)="viewResultImages(result, $event)" [title]="'images.viewImages' | translate">
                                          <i class="fa fa-images"></i>
                                        </button>
                                      }
                                    </div>
                                  </td>
                                } @else {
                                  <!-- Single time column when no multiple rounds -->
                                  <td class="text-end pe-4">
                                    <div class="score-cell">
                                      @if (result.final_time_seconds || result.raw_time_seconds) {
                                        <span class="score-value">{{ formatResultTime(result.final_time_seconds || result.raw_time_seconds) }}</span>
                                        @if (result.penalty_total > 0) {
                                          <span class="penalty-badge">+{{ result.penalty_total }}s</span>
                                        }
                                      } @else if (result.final_points !== null && result.final_points !== undefined) {
                                        <span class="score-value">{{ result.final_points }} pts</span>
                                      } @else if (result.raw_points !== null && result.raw_points !== undefined) {
                                        <span class="score-value">{{ result.raw_points }} pts</span>
                                      } @else if (result.status && result.status !== 'recorded' && result.status !== 'pending') {
                                        <span class="status-badge-result {{ result.status }}">{{ result.status | uppercase }}</span>
                                      } @else {
                                        <span class="text-muted">-</span>
                                      }
                                      @if (result.image_count && result.image_count > 0) {
                                        <button class="btn btn-sm btn-icon btn-light-primary ms-2 image-icon-btn" (click)="viewResultImages(result, $event)" [title]="'images.viewImages' | translate">
                                          <i class="fa fa-images"></i>
                                        </button>
                                      }
                                    </div>
                                  </td>
                                }
                              </tr>
                            }
                          </tbody>
                        </table>
                      </div>
                    } @else {
                      <div class="text-center py-8 text-muted">
                        <i class="fa fa-clipboard-list fs-2x mb-3 d-block"></i>
                        {{ 'eventDetails.noResultsForClass' | translate }}
                      </div>
                    }
                  </div>
                }
              </div>
            }
          </div>
        }
      </div>
    }

    @if (currentView === 'information') {
      <div class="container-xxl py-10" style="max-width: 1400px;">
        <div class="row g-8">
          <!-- Main Content -->
          <div class="col-lg-8">
            <!-- Event Description -->
            <div class="card mb-8" id="overview">
            <div class="card-body p-8">
              <h2 class="card-title fw-bold mb-5">{{ 'eventDetails.aboutEvent' | translate }}</h2>
              <div class="event-description-wrapper" [class.collapsed]="!descriptionExpanded">
                <div class="event-description-content" [innerHTML]="event.event_description"></div>
                @if (!descriptionExpanded) {
                  <div class="description-fade"></div>
                }
              </div>
              <button class="btn btn-link text-primary p-0 mt-3" (click)="toggleDescription()">
                <i class="fa" [class.fa-chevron-down]="!descriptionExpanded" [class.fa-chevron-up]="descriptionExpanded"></i>
                {{ descriptionExpanded ? ('eventDetails.showLess' | translate) : ('eventDetails.readMore' | translate) }}
              </button>
            </div>
          </div>

          <!-- Event Classes (Competition events only, not for social events) -->
          @if (isCompetitionEvent && !isSocialEvent) {
            <div class="card mb-8" id="classes">
              <div class="card-header">
                <h3 class="card-title fw-bold m-0">
                  <i class="fa fa-list text-primary me-3"></i>{{ 'eventDetails.availableClasses' | translate }}
                </h3>
              </div>
              <div class="card-body p-0">
                @if (loadingClasses) {
                  <div class="text-center py-10">
                    <div class="spinner-border spinner-border-sm text-primary"></div>
                  </div>
                } @else if (eventClasses.length > 0) {
                  <!-- Classes grouped by day -->
                  @for (dateGroup of getClassesByDate(); track dateGroup.date) {
                    <div class="classes-day-group">
                      <div class="classes-day-header">
                        <i class="fa fa-calendar me-2"></i>
                        {{ formatScheduleDate(dateGroup.date) }}
                      </div>
                      <div class="table-responsive">
                        <table class="table table-hover mb-0">
                          <thead>
                            <tr class="fw-bold text-muted bg-light">
                              <th class="ps-8">{{ 'eventDetails.className' | translate }}</th>
                              <th>{{ 'eventDetails.startTime' | translate }}</th>
                              <th>{{ 'eventDetails.registrations' | translate }}</th>
                              <th class="pe-8">{{ 'eventDetails.price' | translate }}</th>
                            </tr>
                          </thead>
                          <tbody>
                            @for (eventClass of dateGroup.classes; track eventClass.event_class_id) {
                              <tr>
                                <td class="ps-8">
                                  <div class="fw-semibold">{{ eventClass.class_name }}</div>
                                  @if (eventClass.class_description) {
                                    <div class="text-muted fs-7">{{ eventClass.class_description }}</div>
                                  }
                                </td>
                                <td>
                                  @if (eventClass.start_date_time) {
                                    <span>{{ formatTime(eventClass.start_date_time) }}</span>
                                  } @else {
                                    <span class="text-muted">{{ 'eventDetails.tba' | translate }}</span>
                                  }
                                </td>
                                <td>
                                  <span>{{ getClassRegistrationCount(eventClass) }}</span>
                                </td>
                                <td class="pe-8">
                                  @if (eventClass.price) {
                                    <div class="price-display">
                                      <!-- User has member discount -->
                                      @if (isLoggedIn && hasMemberDiscount('class', eventClass.event_class_id)) {
                                        <span class="original-price">{{ eventClass.price }} {{ eventClass.price_currency || 'kr' }}</span>
                                        <span class="discounted-price">{{ getEffectivePrice('class', eventClass.event_class_id, eventClass.price) }} {{ eventClass.price_currency || 'kr' }}</span>
                                        <span class="savings-badge">-{{ getSavingsPercent('class', eventClass.event_class_id) }}%</span>
                                      }
                                      <!-- User can get member discount -->
                                      @else if (isLoggedIn && canGetMemberDiscount('class', eventClass.event_class_id)) {
                                        <span class="fw-semibold">{{ eventClass.price }} {{ eventClass.price_currency || 'kr' }}</span>
                                        <span class="member-offer-inline">
                                          ↳ <span class="member-link" (click)="openMembershipPurchaseModal('class', eventClass.event_class_id, eventClass.class_name, eventClass.price, eventClass.price_currency || 'kr'); $event.stopPropagation()">{{ getBestMemberPrice('class', eventClass.event_class_id) }} {{ eventClass.price_currency || 'kr' }} {{ 'eventDetails.asMember' | translate }}</span>
                                        </span>
                                      }
                                      <!-- Regular price (no discount available or not logged in) -->
                                      @else {
                                        <span class="fw-semibold">{{ eventClass.price }} {{ eventClass.price_currency || 'kr' }}</span>
                                      }
                                    </div>
                                  } @else {
                                    <span class="text-muted">{{ 'eventDetails.free' | translate }}</span>
                                  }
                                </td>
                              </tr>
                            }
                          </tbody>
                        </table>
                      </div>
                    </div>
                  }
                } @else {
                  <div class="text-center py-10 text-muted">
                    <i class="fa fa-list fs-3x mb-5"></i>
                    <p>{{ 'eventDetails.noClassesAvailable' | translate }}</p>
                  </div>
                }
              </div>
            </div>
          }

          <!-- Training Packages (Training events only) -->
          @if (isTrainingEvent) {
            <div class="card mb-8" id="packages">
              <div class="card-header">
                <h3 class="card-title fw-bold m-0">
                  <i class="fa fa-box-open text-primary me-3"></i>{{ 'eventDetails.availablePackages' | translate }}
                </h3>
              </div>
              <div class="card-body p-0">
                @if (loadingPackages) {
                  <div class="text-center py-10">
                    <div class="spinner-border spinner-border-sm text-primary"></div>
                  </div>
                } @else if (trainingPackages.length > 0) {
                  <div class="table-responsive">
                    <table class="table table-hover mb-0">
                      <thead>
                        <tr class="fw-bold text-muted bg-light">
                          <th class="ps-8">{{ 'eventDetails.packageName' | translate }}</th>
                          <th>{{ 'eventDetails.sessions' | translate }}</th>
                          <th>{{ 'eventDetails.type' | translate }}</th>
                          <th>{{ 'eventDetails.available' | translate }}</th>
                          <th class="pe-8">{{ 'eventDetails.price' | translate }}</th>
                        </tr>
                      </thead>
                      <tbody>
                        @for (pkg of trainingPackages; track pkg.package_id) {
                          <tr>
                            <td class="ps-8">
                              <div class="fw-semibold">{{ pkg.package_name }}</div>
                              @if (pkg.package_description) {
                                <div class="text-muted fs-7">{{ pkg.package_description }}</div>
                              }
                            </td>
                            <td>
                              <span>{{ pkg.session_count }} {{ pkg.session_count > 1 ? ('eventDetails.sessionPlural' | translate) : ('eventDetails.session' | translate) }}</span>
                            </td>
                            <td>
                              <span class="badge badge-light-info">{{ getSessionTypeLabel(pkg.session_type) }}</span>
                            </td>
                            <td>
                              @if (!pkg.is_available) {
                                <span class="badge badge-light-danger">
                                  <i class="fa fa-times me-1"></i>{{ 'eventDetails.full' | translate }}
                                </span>
                              } @else if (pkg.slots_left != null && pkg.slots_left <= 3) {
                                <span class="badge badge-light-warning">
                                  <i class="fa fa-exclamation-triangle me-1"></i>{{ 'eventDetails.fewSlotsLeft' | translate }} ({{ pkg.slots_left }})
                                </span>
                              } @else {
                                <span class="badge badge-light-success">
                                  <i class="fa fa-check me-1"></i>{{ 'eventDetails.available' | translate }}
                                </span>
                              }
                            </td>
                            <td class="pe-8">
                              @if (pkg.price_cents > 0) {
                                <div class="price-display">
                                  <!-- User has member discount -->
                                  @if (isLoggedIn && hasMemberDiscount('package', pkg.package_id)) {
                                    <span class="original-price">{{ formatPackagePrice(pkg.price_cents, pkg.currency) }}</span>
                                    <span class="discounted-price">{{ getEffectivePrice('package', pkg.package_id, pkg.price_cents / 100) }} {{ pkg.currency }}</span>
                                    <span class="savings-badge">-{{ getSavingsPercent('package', pkg.package_id) }}%</span>
                                  }
                                  <!-- User can get member discount -->
                                  @else if (isLoggedIn && canGetMemberDiscount('package', pkg.package_id)) {
                                    <span class="fw-semibold">{{ formatPackagePrice(pkg.price_cents, pkg.currency) }}</span>
                                    <span class="member-offer-inline">
                                      ↳ <span class="member-link" (click)="openMembershipPurchaseModal('package', pkg.package_id, pkg.package_name, pkg.price_cents / 100, pkg.currency); $event.stopPropagation()">{{ getBestMemberPrice('package', pkg.package_id) }} {{ pkg.currency }} {{ 'eventDetails.asMember' | translate }}</span>
                                    </span>
                                  }
                                  <!-- Regular price (no discount available or not logged in) -->
                                  @else {
                                    <span class="fw-semibold">{{ formatPackagePrice(pkg.price_cents, pkg.currency) }}</span>
                                  }
                                </div>
                              } @else {
                                <span class="text-muted">{{ 'eventDetails.free' | translate }}</span>
                              }
                            </td>
                          </tr>
                        }
                      </tbody>
                    </table>
                  </div>
                } @else {
                  <div class="text-center py-10 text-muted">
                    <i class="fa fa-box-open fs-3x mb-5"></i>
                    <p>{{ 'eventDetails.noPackagesAvailable' | translate }}</p>
                  </div>
                }
              </div>
            </div>
          }

          <!-- Event Services -->
          @if (eventServices.length > 0) {
            <div class="card mb-8" id="services">
              <div class="card-header">
                <h3 class="card-title fw-bold m-0">
                  <i class="fa fa-concierge-bell text-success me-3"></i>{{ 'eventDetails.availableServices' | translate }}
                </h3>
              </div>
              <div class="card-body p-0">
                <div class="table-responsive">
                  <table class="table table-hover mb-0">
                    <thead>
                      <tr class="fw-bold text-muted bg-light">
                        <th class="ps-8">{{ 'eventDetails.serviceName' | translate }}</th>
                        <th>{{ 'eventDetails.availability' | translate }}</th>
                        <th class="pe-8">{{ 'eventDetails.price' | translate }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      @for (service of eventServices; track service.event_service_id) {
                        <tr>
                          <td class="ps-8">
                            <div class="fw-semibold">{{ service.event_service_name }}</div>
                            @if (service.event_service_description) {
                              <div class="text-muted fs-7">{{ service.event_service_description }}</div>
                            }
                          </td>
                          <td>
                            @if (service.max_num_registrations) {
                              @if (getServiceAvailableSlots(service) === 0) {
                                <span class="badge badge-light-danger">
                                  <i class="fa fa-times me-1"></i>{{ 'eventDetails.full' | translate }}
                                </span>
                              } @else if (getServiceAvailableSlots(service) <= 3) {
                                <span class="badge badge-light-warning">
                                  <i class="fa fa-exclamation-triangle me-1"></i>{{ 'eventDetails.fewSlotsLeft' | translate }} ({{ getServiceAvailableSlots(service) }})
                                </span>
                              } @else {
                                <span class="badge badge-light-success">
                                  <i class="fa fa-check me-1"></i>{{ 'eventDetails.available' | translate }}
                                </span>
                              }
                            } @else {
                              <span class="text-muted">{{ 'eventDetails.unlimited' | translate }}</span>
                            }
                          </td>
                          <td class="pe-8">
                            @if (service.price) {
                              <div class="price-display">
                                <!-- User has member discount -->
                                @if (isLoggedIn && hasMemberDiscount('service', service.event_service_id)) {
                                  <span class="original-price">{{ service.price }} {{ service.price_currency || 'kr' }}</span>
                                  <span class="discounted-price">{{ getEffectivePrice('service', service.event_service_id, service.price) }} {{ service.price_currency || 'kr' }}</span>
                                  <span class="savings-badge">-{{ getSavingsPercent('service', service.event_service_id) }}%</span>
                                }
                                <!-- User can get member discount -->
                                @else if (isLoggedIn && canGetMemberDiscount('service', service.event_service_id)) {
                                  <span class="fw-semibold">{{ service.price }} {{ service.price_currency || 'kr' }}</span>
                                  <span class="member-offer-inline">
                                    ↳ <span class="member-link" (click)="openMembershipPurchaseModal('service', service.event_service_id, service.event_service_name, service.price, service.price_currency || 'kr'); $event.stopPropagation()">{{ getBestMemberPrice('service', service.event_service_id) }} {{ service.price_currency || 'kr' }} {{ 'eventDetails.asMember' | translate }}</span>
                                  </span>
                                }
                                <!-- Regular price (no discount available or not logged in) -->
                                @else {
                                  <span class="fw-semibold">{{ service.price }} {{ service.price_currency || 'kr' }}</span>
                                }
                              </div>
                            } @else {
                              <span class="text-muted">{{ 'eventDetails.free' | translate }}</span>
                            }
                          </td>
                        </tr>
                      }
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          }

          <!-- Training Schedule (Training events only) -->
          @if (isTrainingEvent && trainingSessions.length > 0) {
            <div class="card mb-8" id="training-schedule">
              <div class="card-header">
                <h3 class="card-title fw-bold m-0">
                  <i class="fa fa-calendar-days text-primary me-3"></i>{{ 'eventDetails.trainingSchedule' | translate }}
                </h3>
              </div>
              <div class="card-body p-0">
                @if (loadingSessions) {
                  <div class="text-center py-10">
                    <div class="spinner-border spinner-border-sm text-primary"></div>
                  </div>
                } @else {
                  <div class="timetable">
                    @for (dateGroup of getTrainingSessionsByDate(); track dateGroup.date) {
                      <div class="timetable-day">
                        <div class="timetable-date-header">
                          <i class="fa fa-calendar me-2"></i>
                          {{ formatScheduleDate(dateGroup.date) }}
                        </div>
                        <div class="timetable-entries">
                          @for (session of dateGroup.sessions; track session.session_id) {
                            <div class="timetable-entry"
                                 [class.break-entry]="session.session_type === 'break'"
                                 [class.group-entry]="session.current_participants > 0 && session.session_type !== 'break'"
                                 [class.expanded]="isTrainingSessionExpanded(session.session_id)"
                                 (click)="session.current_participants > 0 && session.session_type !== 'break' ? toggleTrainingSessionExpand(session.session_id) : null">
                              <div class="timetable-time">
                                @if (session.start_time) {
                                  <span class="time-value">{{ formatTime(session.start_time) }}@if (session.end_time) { - {{ formatTime(session.end_time) }}}</span>
                                } @else {
                                  <span class="time-tba">TBA</span>
                                }
                              </div>
                              <div class="timetable-content">
                                <div class="timetable-header">
                                  <h5 class="timetable-title">
                                    @if (session.session_type === 'break') {
                                      <i class="fa fa-mug-hot text-warning me-2"></i>
                                    }
                                    {{ session.session_name }}
                                    @if (session.current_participants > 0 && session.session_type !== 'break') {
                                      <i class="fa fa-chevron-down expand-icon ms-2"></i>
                                    }
                                  </h5>
                                  <div class="timetable-badges">
                                    <span class="badge {{ getSessionTypeBadgeClass(session.session_type) }}">
                                      <i class="fa {{ getSessionTypeIcon(session.session_type) }} me-1"></i>
                                      {{ formatSessionType(session.session_type) }}
                                    </span>
                                  </div>
                                </div>
                                <div class="timetable-details">
                                  @if (session.session_description) {
                                    <span class="detail-item">
                                      {{ session.session_description }}
                                    </span>
                                  }
                                  @if (session.instructor) {
                                    <span class="detail-item">
                                      <i class="fa fa-user-tie text-muted me-1"></i>{{ session.instructor }}
                                    </span>
                                  }
                                  @if (session.location) {
                                    <span class="detail-item">
                                      <i class="fa fa-location-dot text-muted me-1"></i>{{ session.location }}
                                    </span>
                                  }
                                  @if (session.session_type === 'startlist') {
                                    <span class="detail-item">
                                      <i class="fa fa-users text-muted me-1"></i>
                                      {{ session.current_participants }} {{ 'eventDetails.participants' | translate }}
                                    </span>
                                  } @else if (session.max_participants && session.session_type !== 'break') {
                                    <span class="detail-item">
                                      <i class="fa fa-users text-muted me-1"></i>
                                      {{ session.current_participants }}/{{ session.max_participants }} {{ 'eventDetails.participants' | translate }}
                                    </span>
                                  }
                                  @if (session.time_per_participant) {
                                    <span class="detail-item">
                                      <i class="fa fa-clock text-muted me-1"></i>{{ session.time_per_participant }} {{ 'eventDetails.minPerParticipant' | translate }}
                                    </span>
                                  }
                                </div>
                                <!-- Participants list (expandable) -->
                                @if (session.current_participants > 0 && session.session_type !== 'break') {
                                  <div class="group-participants-tooltip">
                                    <div class="participants-header">
                                      <i class="fa fa-users me-2"></i>{{ 'eventDetails.participantsList' | translate }}
                                    </div>
                                    <ul class="participants-list">
                                      @for (participant of getSessionParticipants(session.session_id); track $index) {
                                        <li>
                                          <span class="participant-name">{{ participant.name }}</span>
                                          @if (participant.horse) {
                                            <span class="participant-horse">{{ 'eventDetails.on' | translate }} {{ participant.horse }}</span>
                                          }
                                        </li>
                                      }
                                    </ul>
                                  </div>
                                }
                              </div>
                            </div>
                          }
                        </div>
                      </div>
                    }
                  </div>
                }
              </div>
            </div>
          }

          <!-- Event Schedule / Timetable (Competition events only, not for social events) -->
          @if (isCompetitionEvent && !isSocialEvent) {
            <div class="card mb-8" id="schedules">
              <div class="card-header">
                <h3 class="card-title fw-bold m-0">
                  <i class="fa fa-calendar-days text-primary me-3"></i>{{ 'eventDetails.eventSchedule' | translate }}
                </h3>
              </div>
              <div class="card-body p-0">
                @if (loadingSchedules || loadingClasses) {
                  <div class="text-center py-10">
                    <div class="spinner-border spinner-border-sm text-primary"></div>
                  </div>
                } @else if (eventSchedules.length === 0 && eventClasses.length === 0) {
                  <div class="text-center py-10 text-muted">
                    <i class="fa fa-calendar-days fs-3x mb-5 d-block"></i>
                    <p>{{ 'eventDetails.noScheduleYet' | translate }}</p>
                  </div>
                } @else if (eventSchedules.length === 0) {
                  <!-- Show class-based schedule when no explicit schedules exist -->
                  <div class="timetable">
                    @for (dateGroup of getClassesByDate(); track dateGroup.date) {
                      <div class="timetable-day">
                        <div class="timetable-date-header">
                          <i class="fa fa-calendar me-2"></i>
                          {{ formatScheduleDate(dateGroup.date) }}
                        </div>
                        <div class="timetable-entries">
                          @for (eventClass of dateGroup.classes; track eventClass.event_class_id) {
                            <div class="timetable-entry">
                              <div class="timetable-time">
                                @if (eventClass.start_date_time) {
                                  <span class="time-value">{{ formatTime(eventClass.start_date_time) }}</span>
                                } @else {
                                  <span class="time-tba">TBA</span>
                                }
                              </div>
                              <div class="timetable-content">
                                <div class="timetable-header">
                                  <h5 class="timetable-title">{{ eventClass.class_name }}</h5>
                                  <div class="timetable-badges">
                                    <span class="badge badge-light-primary">
                                      <i class="fa fa-users me-1"></i>{{ getClassRegistrationCount(eventClass) }} {{ 'eventDetails.registered' | translate }}
                                    </span>
                                  </div>
                                </div>
                                @if (eventClass.class_description) {
                                  <div class="timetable-details">
                                    <span class="detail-item text-muted">{{ eventClass.class_description }}</span>
                                  </div>
                                }
                              </div>
                            </div>
                          }
                        </div>
                      </div>
                    }
                  </div>
                } @else {
                  <!-- Timetable View -->
                  <div class="timetable">
                    @for (dateGroup of getSchedulesByDate(); track dateGroup.date) {
                      <div class="timetable-day">
                        <div class="timetable-date-header">
                          <i class="fa fa-calendar me-2"></i>
                          {{ formatScheduleDate(dateGroup.date) }}
                        </div>
                        <div class="timetable-entries">
                          @for (schedule of dateGroup.schedules; track schedule.schedule_id) {
                            <!-- Group Session Schedule - Show each group separately -->
                            @if (schedule.session_type === 'group' && getScheduleGroups(schedule).length > 0) {
                              <div class="timetable-entry group-session-header">
                                <div class="timetable-time">
                                  @if (schedule.schedule_start_time) {
                                    <span class="time-value">{{ formatTime(schedule.schedule_start_time) }}</span>
                                  } @else {
                                    <span class="time-tba">TBA</span>
                                  }
                                </div>
                                <div class="timetable-content">
                                  <div class="timetable-header">
                                    <h5 class="timetable-title">{{ getScheduleClassName(schedule) }}</h5>
                                    <div class="timetable-badges">
                                      <!-- TEMPORARILY DISABLED FOR V1
                                      @if (schedule.status === 'live') {
                                        <span class="badge badge-danger">
                                          <i class="fa fa-broadcast-tower me-1 animate-pulse"></i>LIVE
                                        </span>
                                      }
                                      -->
                                      @if (schedule.track) {
                                        <span class="badge badge-light-info">
                                          <i class="fa fa-flag me-1"></i>{{ schedule.track.track_name }}
                                        </span>
                                      }
                                      <span class="badge badge-light-secondary">
                                        <i class="fa fa-users me-1"></i>{{ 'eventDetails.groupSessions' | translate }}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <!-- Individual Groups -->
                              @for (block of getScheduleBlocks(schedule); track $index) {
                                @if (block.type === 'group') {
                                  <div class="timetable-entry group-entry"
                                       [class.expanded]="isGroupExpanded(schedule.schedule_id, block.groupId)"
                                       (click)="toggleGroupExpand(schedule.schedule_id, block.groupId)">
                                    <div class="timetable-time">
                                      @if (block.startTime) {
                                        <span class="time-value-small">{{ formatTime(block.startTime) }}</span>
                                        <span class="time-end">- {{ formatTime(block.endTime) }}</span>
                                      } @else {
                                        <span class="time-tba">TBA</span>
                                      }
                                    </div>
                                    <div class="timetable-content">
                                      <div class="group-name">
                                        <i class="fa fa-user-group text-primary me-2"></i>
                                        {{ block.name }}
                                        <i class="fa fa-chevron-down expand-icon ms-2"></i>
                                      </div>
                                      <div class="timetable-details">
                                        <span class="detail-item">
                                          <i class="fa fa-users text-muted me-1"></i>{{ block.participantCount }} {{ 'eventDetails.participants' | translate }}
                                        </span>
                                        <span class="detail-item">
                                          <i class="fa fa-clock text-muted me-1"></i>{{ block.durationMinutes }} {{ 'eventDetails.min' | translate }}
                                        </span>
                                      </div>
                                      <!-- Participants tooltip (desktop hover) / expandable list (mobile click) -->
                                      @if (block.participants && block.participants.length > 0) {
                                        <div class="group-participants-tooltip">
                                          <div class="participants-header">
                                            <i class="fa fa-users me-2"></i>{{ 'eventDetails.participantsList' | translate }}
                                          </div>
                                          <ul class="participants-list">
                                            @for (participant of block.participants; track participant.entry_id) {
                                              <li>
                                                <span class="participant-name">{{ participant.name }}</span>
                                                @if (participant.horse) {
                                                  <span class="participant-horse">{{ 'eventDetails.on' | translate }} {{ participant.horse }}</span>
                                                }
                                              </li>
                                            }
                                          </ul>
                                        </div>
                                      }
                                    </div>
                                  </div>
                                } @else if (block.type === 'break') {
                                  <div class="timetable-entry break-entry">
                                    <div class="timetable-time">
                                      <span class="time-break">{{ block.durationMinutes }} {{ 'eventDetails.min' | translate }}</span>
                                    </div>
                                    <div class="timetable-content">
                                      <div class="break-label">
                                        <i class="fa fa-mug-hot text-warning me-2"></i>
                                        {{ block.notes || ('eventDetails.break' | translate) }}
                                      </div>
                                    </div>
                                  </div>
                                }
                              }
                            } @else {
                              <!-- Standard Schedule Entry (start list or individual lessons) -->
                              <div class="timetable-entry">
                                <div class="timetable-time">
                                  @if (schedule.schedule_start_time) {
                                    <span class="time-value">{{ formatTime(schedule.schedule_start_time) }}</span>
                                  } @else {
                                    <span class="time-tba">TBA</span>
                                  }
                                </div>
                                <div class="timetable-content">
                                  <div class="timetable-header">
                                    <h5 class="timetable-title">{{ getScheduleClassName(schedule) }}</h5>
                                    <div class="timetable-badges">
                                      <!-- TEMPORARILY DISABLED FOR V1
                                      @if (schedule.status === 'live') {
                                        <span class="badge badge-danger">
                                          <i class="fa fa-broadcast-tower me-1 animate-pulse"></i>LIVE
                                        </span>
                                      }
                                      -->
                                      @if (schedule.track) {
                                        <span class="badge badge-light-info">
                                          <i class="fa fa-flag me-1"></i>{{ schedule.track.track_name }}
                                        </span>
                                      }
                                      <span class="badge badge-light-secondary">
                                        <i class="fa {{ getScheduleTypeIcon(schedule) }} me-1"></i>
                                        {{ getScheduleTypeLabel(schedule) }}
                                      </span>
                                    </div>
                                  </div>
                                  <div class="timetable-details">
                                    @if (schedule.schedule_name && schedule.schedule_name !== getScheduleClassName(schedule)) {
                                      <span class="detail-item">
                                        <i class="fa fa-tag text-muted me-1"></i>{{ schedule.schedule_name }}
                                      </span>
                                    }
                                    <span class="detail-item">
                                      <i class="fa fa-users text-muted me-1"></i>{{ getTotalCount(schedule) }} {{ 'eventDetails.participants' | translate }}
                                    </span>
                                    @if (schedule.time_per_entry_seconds) {
                                      <span class="detail-item">
                                        <i class="fa fa-clock text-muted me-1"></i>{{ formatDuration(schedule.time_per_entry_seconds) }} {{ 'eventDetails.perEntry' | translate }}
                                      </span>
                                    }
                                    @if (schedule.schedule_start_time && schedule.time_per_entry_seconds) {
                                      <span class="detail-item">
                                        <i class="fa fa-hourglass-end text-muted me-1"></i>~{{ getEstimatedEndTime(schedule) }}
                                      </span>
                                    }
                                  </div>
                                  <!-- Start List - Collapsible (inside card) -->
                                  @if (getAllParticipants(schedule).length > 0) {
                                    <details class="schedule-startlist-card">
                                      <summary class="startlist-toggle-card">
                                        <i class="fa fa-list-ol me-2"></i>
                                        {{ 'eventDetails.viewStartList' | translate }}
                                        <span class="participant-count">({{ getAllParticipants(schedule).length }})</span>
                                        <i class="fa fa-chevron-down toggle-icon"></i>
                                      </summary>
                                      <div class="startlist-table">
                                        @for (entry of getStartListEntries(schedule); track entry.entry_id) {
                                          @if (entry.entry_type === 'break') {
                                            <div class="startlist-row startlist-break">
                                              <span class="startlist-position"><i class="fa fa-coffee text-muted"></i></span>
                                              <span class="startlist-time">
                                                @if (entry.scheduled_time || entry.start_time) {
                                                  {{ formatTime(entry.scheduled_time || entry.start_time) }}
                                                } @else {
                                                  --:--
                                                }
                                              </span>
                                              <span class="startlist-name">
                                                {{ entry.notes || ('eventDetails.break' | translate) }}
                                                @if (entry.duration_seconds) {
                                                  <span class="text-muted"> ({{ formatBreakDuration(entry.duration_seconds) }})</span>
                                                }
                                              </span>
                                            </div>
                                          } @else {
                                            <div class="startlist-row" [class]="getEntryStatusClass(entry, schedule)">
                                              <span class="startlist-position">{{ entry.position }}</span>
                                              <span class="startlist-time">
                                                @if (entry.scheduled_time || entry.start_time) {
                                                  {{ formatTime(entry.scheduled_time || entry.start_time) }}
                                                } @else {
                                                  --:--
                                                }
                                              </span>
                                              <span class="startlist-name">{{ getEntryDisplayName(entry) }}</span>
                                            </div>
                                          }
                                        }
                                      </div>
                                    </details>
                                  }
                                </div>
                              </div>
                            }
                          }
                        </div>
                      </div>
                    }
                  </div>
                }
              </div>
            </div>
          }

          <!-- TEMPORARILY DISABLED FOR V1
          Live Start Lists (Competition events only, not for social events)
          @if (isCompetitionEvent && !isSocialEvent && hasLiveSchedules()) {
            <div class="card mb-8" id="live">
              <div class="card-header d-flex justify-content-between align-items-center">
                <h3 class="card-title fw-bold m-0">
                  <i class="fa fa-broadcast-tower text-danger me-3 animate-pulse"></i>
                  <span class="text-danger">{{ 'eventDetails.liveStartLists' | translate }}</span>
                </h3>
                <span class="badge badge-light-success">{{ 'eventDetails.autoRefreshing' | translate }}</span>
              </div>
              <div class="card-body">
                @for (schedule of getLiveSchedules(); track schedule.schedule_id) {
                  <div class="live-schedule-card mb-4">
                    <div class="live-schedule-header">
                      <div class="live-schedule-info">
                        <h4 class="mb-0">{{ getScheduleClassName(schedule) }}</h4>
                        @if (schedule.track) {
                          <span class="fs-7 text-muted">
                            <i class="fa fa-flag me-1"></i>{{ schedule.track.track_name }}
                          </span>
                        }
                      </div>
                      <div class="live-badge-progress">
                        <span class="badge badge-danger">
                          <i class="fa fa-circle me-1 animate-pulse"></i>LIVE
                        </span>
                        <span class="progress-text">{{ schedule.current_entry_position || 1 }}/{{ getTotalCount(schedule) }}</span>
                      </div>
                    </div>

                    Current Participant Highlight
                    @if (getCurrentEntry(schedule)) {
                      <div class="current-rider-highlight">
                        <div class="current-label">
                          <i class="fa fa-circle-dot text-danger me-1"></i>
                          {{ 'eventDetails.nowRiding' | translate }}
                        </div>
                        <div class="current-rider-info">
                          <span class="position-badge large">{{ getCurrentEntry(schedule)!.position }}</span>
                          <span class="rider-name">{{ getEntryDisplayName(getCurrentEntry(schedule)!) }}</span>
                        </div>
                      </div>
                    }

                    All Participants List
                    <div class="participants-startlist">
                      <div class="startlist-header">
                        <i class="fa fa-list-ol me-2"></i>{{ 'eventDetails.startList' | translate }}
                      </div>
                      <div class="startlist-scroll">
                        @for (entry of getAllParticipants(schedule); track entry.entry_id) {
                          <div class="startlist-row" [class]="getEntryStatusClass(entry, schedule)">
                            <span class="startlist-position">{{ entry.position }}</span>
                            <span class="startlist-name">{{ getEntryDisplayName(entry) }}</span>
                            <span class="startlist-time">
                              @if (entry.scheduled_time || entry.start_time) {
                                {{ formatTime(entry.scheduled_time || entry.start_time) }}
                              } @else {
                                - -:- -
                              }
                            </span>
                            <span class="startlist-status">
                              @switch (getEntryStatusClass(entry, schedule)) {
                                @case ('current') {
                                  <i class="fa fa-circle-dot text-danger"></i>
                                }
                                @case ('completed') {
                                  <i class="fa fa-check text-success"></i>
                                }
                                @case ('scratched') {
                                  <i class="fa fa-times text-danger"></i>
                                }
                                @default {
                                  <i class="fa fa-clock text-muted"></i>
                                }
                              }
                            </span>
                          </div>
                        }
                      </div>
                    </div>
                  </div>
                }
              </div>
            </div>
          }
          -->

        </div>

        <!-- Sidebar -->
        <div class="col-lg-4">
          <div class="sidebar-sticky-wrapper">
            <!-- Registration Card -->
            <div class="card mb-8 registration-card-sticky">
              <div class="card-body p-8">
                <h3 class="fw-bold mb-6">{{ 'eventDetails.eventRegistration' | translate }}</h3>

                <!-- Registration Deadline -->
                @if (event.registration_deadline) {
                  @if (isRegistrationClosed) {
                    <div class="alert alert-danger d-flex align-items-center mb-6">
                      <i class="fa fa-lock fs-2x me-3"></i>
                      <div>
                        <div class="fw-semibold">{{ 'eventDetails.registrationClosed' | translate }}</div>
                        <div class="fs-7">{{ 'eventDetails.deadlineWas' | translate }} {{ formatDateDMYDots(event.registration_deadline) }}</div>
                      </div>
                    </div>
                  } @else {
                    <div class="alert alert-warning d-flex align-items-center mb-6">
                      <i class="fa fa-clock fs-2x me-3"></i>
                      <div>
                        <div class="fw-semibold">{{ 'eventDetails.registrationDeadline' | translate }}</div>
                        <div class="fs-7">{{ formatDateDMYDots(event.registration_deadline) }}</div>
                      </div>
                    </div>
                  }
                }

                @if (!isRegistrationClosed) {
                  @if (isLoggedIn) {
                    <div class="d-flex gap-3">
                      <!-- Register Button -->
                      <app-button
                        [variant]="'primary'"
                        [label]="'eventDetails.registerToEvent' | translate"
                        [iconClass]="'fa fa-check-circle'"
                        class="flex-fill"
                        (clicked)="registerToEvent()"
                      />

                      <!-- Send Message Button -->
                      <app-button
                        [variant]="'outline'"
                        [label]="'eventDetails.sendMessage' | translate"
                        [iconClass]="'fa fa-comments'"
                        class="flex-fill"
                        (clicked)="openSendMessage()"
                      />
                    </div>
                  } @else {
                    <div class="alert alert-light-primary d-flex align-items-center mb-0">
                      <i class="fa fa-sign-in-alt fs-4 me-3 text-primary"></i>
                      <div>
                        <a routerLink="/auth/login" class="fw-semibold text-primary">{{ 'eventDetails.logIn' | translate }}</a> {{ 'eventDetails.orText' | translate }}
                        <a routerLink="/auth/register-user" class="fw-semibold text-primary">{{ 'eventDetails.register' | translate }}</a>
                        {{ 'eventDetails.loginToRegister' | translate }}
                      </div>
                    </div>
                  }
                } @else {
                  <!-- Send Message Button still available after deadline -->
                  @if (isLoggedIn) {
                    <app-button
                      [variant]="'outline'"
                      [label]="'eventDetails.sendMessage' | translate"
                      [iconClass]="'fa fa-comments'"
                      class="w-100"
                      (clicked)="openSendMessage()"
                    />
                  }
                }

                <div class="separator my-6"></div>

                <!-- Event Info -->
                <div class="event-info-list">
                  <div class="event-info-item mb-5">
                    <div class="event-info-label">
                      <i class="fa fa-calendar text-primary me-2"></i>
                      <span class="fw-semibold">{{ 'eventDetails.dates' | translate }}</span>
                    </div>
                    <div class="event-info-value">
                      {{ formatDateDMYDots(event.start_date) }}
                      @if (event.end_date && event.start_date !== event.end_date) {
                        <br>{{ 'event.dateTo' | translate }} {{ formatDateDMYDots(event.end_date) }}
                      }
                    </div>
                  </div>

                  <div class="event-info-item mb-5">
                    <div class="event-info-label">
                      <i class="fa fa-location-dot text-primary me-2"></i>
                      <span class="fw-semibold">{{ 'eventDetails.location' | translate }}</span>
                    </div>
                    <div class="event-info-value">
                      <div>{{ event.city }}, {{ event.country }}</div>
                      @if (event.address) {
                        <a [href]="getGoogleMapsUrl(event)" target="_blank" class="text-primary text-decoration-none fs-7">
                          {{ event.address }}
                          <i class="fa fa-external-link ms-1"></i>
                        </a>
                      }
                    </div>
                  </div>

                  <!-- Map -->
                  @if (mapMarker) {
                    <div class="event-info-item">
                      <app-location-map
                        [markers]="[mapMarker]"
                        [config]="mapConfig"
                        [loading]="geocodingInProgress"
                      />
                    </div>
                  }
                </div>
              </div>
            </div>
          </div>

          <!-- Event Contacts -->
          @if (eventContacts.length > 0) {
            <div class="card" id="contacts">
              <div class="card-body p-8">
                <h3 class="fw-bold mb-6">{{ 'eventDetails.eventContacts' | translate }}</h3>

                @if (loadingContacts) {
                  <div class="text-center py-5">
                    <div class="spinner-border spinner-border-sm text-primary"></div>
                  </div>
                } @else {
                  <div class="d-flex flex-column gap-4">
                    @for (contact of eventContacts; track contact.contact_id) {
                      <div class="contact-card">
                        <div class="d-flex justify-content-between align-items-start mb-3">
                          <div class="flex-grow-1">
                            <h4 class="mb-1 fs-5 fw-semibold">{{ contact.first_name }} {{ contact.last_name }}</h4>
                            <span class="badge badge-light-primary">{{ contact.role?.role_name || 'N/A' }}</span>
                          </div>
                        </div>

                        <div class="separator mb-3"></div>

                        <div class="d-flex flex-column gap-2">
                          @if (contact.email) {
                            <div class="d-flex align-items-center">
                              <i class="fa fa-envelope text-muted me-2"></i>
                              <a [href]="'mailto:' + contact.email" class="text-gray-700 text-decoration-none">{{ contact.email }}</a>
                            </div>
                          }
                          @if (contact.phone) {
                            <div class="d-flex align-items-center">
                              <i class="fa fa-phone text-muted me-2"></i>
                              <a [href]="'tel:' + contact.phone" class="text-gray-700 text-decoration-none">{{ contact.phone }}</a>
                            </div>
                          }
                        </div>
                      </div>
                    }
                  </div>
                }
              </div>
            </div>
          }
        </div>
      </div>
    </div>

    <!-- Sponsors Section -->
    @if (eventSponsors.length > 0) {
      <div class="sponsors-section">
        <h3>{{ 'eventDetails.sponsoredBy' | translate }}</h3>
        <div class="sponsors-marquee-container">
          <div class="sponsors-marquee">
            <div class="sponsors-track" [class.animate]="shouldAnimateSponsors()" [class.static]="!shouldAnimateSponsors()">
              @for (eventSponsor of eventSponsors; track eventSponsor.entry_id) {
                @if (eventSponsor.sponsor) {
                  <div class="sponsor-slide">
                    <div class="sponsor-card-animated">
                      <div class="sponsor-badge">
                        @if (eventSponsor.sponsor.logo_url) {
                          <img [src]="eventSponsor.sponsor.logo_url"
                               [alt]="eventSponsor.sponsor.sponsor_name"
                               class="sponsor-logo-animated" />
                        }
                        <div class="sponsor-shine"></div>
                      </div>
                      <div class="sponsor-details">
                        <h4>{{ eventSponsor.sponsor.sponsor_name }}</h4>
                        @if (eventSponsor.sponsor.sponsor_description) {
                          <p>{{ eventSponsor.sponsor.sponsor_description }}</p>
                        }
                      </div>
                    </div>
                  </div>
                }
              }
              <!-- Duplicate for seamless loop (only when animating) -->
              @if (shouldAnimateSponsors()) {
                @for (eventSponsor of eventSponsors; track eventSponsor.entry_id) {
                  @if (eventSponsor.sponsor) {
                    <div class="sponsor-slide">
                      <div class="sponsor-card-animated">
                        <div class="sponsor-badge">
                          @if (eventSponsor.sponsor.logo_url) {
                            <img [src]="eventSponsor.sponsor.logo_url"
                                 [alt]="eventSponsor.sponsor.sponsor_name"
                                 class="sponsor-logo-animated" />
                          }
                          <div class="sponsor-shine"></div>
                        </div>
                        <div class="sponsor-details">
                          <h4>{{ eventSponsor.sponsor.sponsor_name }}</h4>
                          @if (eventSponsor.sponsor.sponsor_description) {
                            <p>{{ eventSponsor.sponsor.sponsor_description }}</p>
                          }
                        </div>
                      </div>
                    </div>
                  }
                }
              }
            </div>
          </div>
        </div>
      </div>
    }
    } <!-- End of currentView === 'information' -->
  </div>
}

<!-- Event Not Found -->
@if (!loading && !event) {
  <div class="container-xxl py-20">
    <div class="text-center">
      <i class="fa fa-calendar-xmark fs-3x text-muted mb-5"></i>
      <h3 class="text-muted">{{ 'eventDetails.notFound' | translate }}</h3>
      <p class="text-muted">{{ 'eventDetails.notFoundHint' | translate }}</p>
      <app-button
        [variant]="'primary'"
        [label]="'eventDetails.backToEvents' | translate"
        [routerLink]="'/shows'"
      />
    </div>
  </div>
}

<!-- Image Lightbox -->
@if (lightboxImage) {
  <div class="lightbox-overlay" (click)="closeLightbox()">
    <button class="lightbox-close" (click)="closeLightbox()">
      <i class="fa fa-times"></i>
    </button>
    <div class="lightbox-content" (click)="$event.stopPropagation()">
      <img [src]="lightboxImage" alt="Full size image">
    </div>
  </div>
}
